import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Row, Col, Form, Button, Spinner, Badge } from "react-bootstrap";
import Topnav from "./Topnav";
import Footer from "./Footer";
import { Link } from "react-router-dom";
import bannerImage from "../../Images/programs/Post-Graduation.webp";
import EnquiryNow from "../common/EnquiryNow";

const SCHOOLS_API = "https://isbmuniversity.edu.in/admin-portal/schoolsApi";
const COURSES_API = (schoolSlug) =>
  `https://isbmuniversity.edu.in/admin-portal/coursesList/${schoolSlug}`;

const normalize = (s) => (s || "").trim();

const parseDurationToMonths = (durationRaw) => {
  // Examples:
  // "3 Years" -> 36, "2 Years (Full Time)" -> 24, "18 Months" -> 18, "1 Year" -> 12
  const txt = (durationRaw || "").toLowerCase();
  const years = txt.match(/(\d+)\s*year/);
  const months = txt.match(/(\d+)\s*month/);
  let total = 0;
  if (years) total += parseInt(years[1], 10) * 12;
  if (months) total += parseInt(months[1], 10);
  return total || null;
};

const durationBucket = (months) => {
  if (!months) return "Unknown";
  if (months <= 12) return "≤ 1 year";
  if (months <= 24) return "≤ 2 years";
  if (months <= 36) return "≤ 3 years";
  if (months <= 48) return "≤ 4 years";
  return "> 4 years";
};

const uniq = (arr) =>
  Array.from(new Set(arr.filter(Boolean).map((s) => s.trim()))).sort();

const CourseFinderPage = () => {
  useEffect(() => {
    const schemas = [
      {
        id: "webpage-schema",
        data: {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": "https://isbmuniversity.edu.in/all-courses#webpage",
          url: "https://isbmuniversity.edu.in/all-courses",
          name: "Which Programme Should You Choose? Find by Qualification | ISBM University",
          description:
            "Not sure which academic course fits your qualifications? Filter UG, PG & Diploma programmes by your education level and find your best fit for career growth & success.",
          isPartOf: {
            "@type": "WebSite",
            "@id": "https://isbmuniversity.edu.in/#website",
            name: "ISBM University",
            url: "https://isbmuniversity.edu.in/",
          },
          inLanguage: "en-IN",
        },
      },

      {
        id: "college-or-university-schema",
        data: {
          "@context": "https://schema.org",
          "@type": "CollegeOrUniversity",
          "@id": "https://isbmuniversity.edu.in/#collegeOrUniversity",
          name: "ISBM University | Best Private University in Chhattisgarh",
          url: "https://isbmuniversity.edu.in/",
          logo: "https://isbmuniversity.edu.in/static/media/isbm-university.9ceb30717bf3ba95be6a.webp",
          image:
            "https://isbmuniversity.edu.in/static/media/isbm-university.9ceb30717bf3ba95be6a.webp",
          description:
            "ISBM University is a private university in Chhattisgarh offering UG, PG, diploma and Ph.D. programs with industry focused education and career opportunities.",
          telephone: "+91-9373199999",
          email: "info@isbmuniversity.edu.in",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Nawapara (Kosmi), Block & Tehsil- Chhura",
            addressLocality: "Gariyaband",
            addressRegion: "Chhattisgarh",
            postalCode: "493996",
            addressCountry: "IN",
          },
          sameAs: [
            "https://www.facebook.com/isbmuniversity/",
            "https://www.instagram.com/isbm_university",
            "https://www.linkedin.com/school/13211303/admin/",
          ],
        },
      },

      {
        id: "local-business-schema",
        data: {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "ISBM University",
          image:
            "https://isbmuniversity.edu.in/static/media/isbm-university.9ceb30717bf3ba95be6a.webp",
          "@id": "https://isbmuniversity.edu.in/#organization",
          url: "https://isbmuniversity.edu.in/",
          telephone: "+91-9373199999",
          location: [
            {
              "@type": "Place",
              name: "ISBM University - Raipur Off-Campus",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Ring Road No. 1, Near Sarona Bridge, Tatibandh",
                addressLocality: "Raipur",
                addressRegion: "Chhattisgarh",
                postalCode: "492099",
                addressCountry: "IN",
              },
            },
            {
              "@type": "Place",
              name: "ISBM University - Main Campus",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Nawapara (Kosmi), Block & Tehsil - Chhura",
                addressLocality: "Gariyaband",
                addressRegion: "Chhattisgarh",
                postalCode: "493996",
                addressCountry: "IN",
              },
            },
          ],
        },
      },

      {
        id: "breadcrumb-schema",
        data: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://isbmuniversity.edu.in/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "All Courses",
              item: "https://isbmuniversity.edu.in/all-courses",
            },
          ],
        },
      },
    ];

    const createdScripts = [];

    schemas.forEach(({ id, data }) => {
      const existingScript = document.head.querySelector(
        `script[data-schema-id="${id}"]`,
      );

      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement("script");

      script.type = "application/ld+json";
      script.setAttribute("data-schema-id", id);
      script.textContent = JSON.stringify(data);

      document.head.appendChild(script);

      createdScripts.push(script);
    });

    return () => {
      createdScripts.forEach((script) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);

  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [errorSchools, setErrorSchools] = useState("");

  const [coursesCache, setCoursesCache] = useState({}); // { [schoolSlug]: { data: [], loaded: true } }
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [errorCourses, setErrorCourses] = useState("");

  // Filters
  const [selectedSchool, setSelectedSchool] = useState("all"); // 'all' or school.name
  const [selectedDept, setSelectedDept] = useState("all");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [selectedMode, setSelectedMode] = useState("all");
  const [eligibilityText, setEligibilityText] = useState("");
  const [selectedDuration, setSelectedDuration] = useState("all"); // bucket or 'all'
  const [showOnlyRaipur, setShowOnlyRaipur] = useState(false);
  const [showOnlyGariyaband, setShowOnlyGariyaband] = useState(false);

  const abortRef = useRef(null);

  const [modalShow2, setModalShow2] = useState(false);

  // 1) Load all schools
  useEffect(() => {
    const run = async () => {
      setLoadingSchools(true);
      setErrorSchools("");
      try {
        const { data } = await axios.get(SCHOOLS_API);
        const list = data?.data || [];
        setSchools(list);
      } catch (e) {
        setErrorSchools("Failed to load schools.");
        console.error(e);
      } finally {
        setLoadingSchools(false);
      }
    };
    run();
  }, []);

  // 2) Load courses for needed schools (selected or all)
  const schoolSlugsToEnsure = useMemo(() => {
    if (selectedSchool === "all") return schools.map((s) => s.name);
    return schools.filter((s) => s.name === selectedSchool).map((s) => s.name);
  }, [schools, selectedSchool]);

  useEffect(() => {
    if (schoolSlugsToEnsure.length === 0) return;
    let canceled = false;

    const load = async () => {
      setLoadingCourses(true);
      setErrorCourses("");
      try {
        // fetch sequentially to be gentle on API; cache results
        for (const slug of schoolSlugsToEnsure) {
          if (canceled) return;
          if (coursesCache[slug]?.loaded) continue;
          const { data } = await axios.get(COURSES_API(slug));
          const list = Array.isArray(data?.data) ? data.data : [];
          setCoursesCache((prev) => ({
            ...prev,
            [slug]: { data: list, loaded: true },
          }));
        }
      } catch (e) {
        setErrorCourses("Failed to load courses.");
        console.error(e);
      } finally {
        if (!canceled) setLoadingCourses(false);
      }
    };

    load();
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolSlugsToEnsure.join("|")]); // join to trigger when set changes

  // 3) Aggregate courses for current scope (selected school or all)
  const scopedCourses = useMemo(() => {
    const slugs = schoolSlugsToEnsure;
    let agg = [];
    slugs.forEach((slug) => {
      const pack = coursesCache[slug];
      if (pack?.data?.length) agg = agg.concat(pack.data);
    });
    return agg;
  }, [schoolSlugsToEnsure, coursesCache]);

  // 4) Build dynamic filter option lists from data
  const levelOptions = useMemo(
    () => ["all", ...uniq(scopedCourses.map((c) => c.level))],
    [scopedCourses],
  );
  const modeOptions = useMemo(
    () => ["all", ...uniq(scopedCourses.map((c) => c.mode))],
    [scopedCourses],
  );
  const departmentOptions = useMemo(() => {
    const depts = uniq(scopedCourses.map((c) => c.department));
    return ["all", ...depts];
  }, [scopedCourses]);

  const durationBucketsFound = useMemo(() => {
    const buckets = uniq(
      scopedCourses
        .map((c) => durationBucket(parseDurationToMonths(c.duration)))
        .filter(Boolean),
    );
    // keep a stable order
    const order = [
      "≤ 1 year",
      "≤ 2 years",
      "≤ 3 years",
      "≤ 4 years",
      "> 4 years",
      "Unknown",
    ];
    const sorted = order.filter((b) => buckets.includes(b));
    return ["all", ...sorted];
  }, [scopedCourses]);

  // 5) Apply filters
  const filteredCourses = useMemo(() => {
    const q = eligibilityText.trim().toLowerCase();

    return scopedCourses.filter((c) => {
      // Raipur/Gariyaband campus availability (img tag accept/close inside)
      if (
        showOnlyRaipur &&
        !String(c.raipur_campus || "").includes("accept.png")
      )
        return false;
      if (
        showOnlyGariyaband &&
        !String(c.gariyaband_campus || "").includes("accept.png")
      )
        return false;

      if (selectedLevel !== "all" && normalize(c.level) !== selectedLevel)
        return false;
      if (selectedMode !== "all" && normalize(c.mode) !== selectedMode)
        return false;
      if (selectedDept !== "all" && normalize(c.department) !== selectedDept)
        return false;
      if (
        selectedSchool !== "all" &&
        normalize(c.school_name) !== selectedSchool
      )
        return false;

      if (selectedDuration !== "all") {
        const months = parseDurationToMonths(c.duration);
        const bucket = durationBucket(months);
        if (bucket !== selectedDuration) return false;
      }

      if (q) {
        // match inside eligibility string OR title
        const hay = `${c.eligibility || ""} ${c.title || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [
    scopedCourses,
    selectedLevel,
    selectedMode,
    selectedDept,
    selectedSchool,
    selectedDuration,
    eligibilityText,
    showOnlyRaipur,
    showOnlyGariyaband,
  ]);

  // UI
  return (
    <React.Fragment>
      <title>
        Explore All Courses & Programmes | ISBM University Chhattisgarh
      </title>
      <meta
        name="description"
        content="Find all courses and programmes at ISBM University in Raipur and Gariyaband, including 100+ UG, PG, diploma, certificate and Ph.D. programmes across diverse disciplines."
      />
      <Topnav />
      <section
        className="section-hero-banner ptb-50"
        style={{
          backgroundImage: `linear-gradient(rgba(28,36,71,0.8), rgba(77, 77, 77, 0.8)), url(${bannerImage})`,
        }}
      >
        <div className="banner-hero-overlay">
          <div className="container-xxl">
            <div className="section-hero-content">
              <h1>All Courses</h1>
              <ol className="breadcrumb mt-3 text-center mb-0">
                <li className="breadcrumb-item">
                  <Link
                    to="/"
                    style={{ color: "#fff", textDecoration: "none" }}
                  >
                    Home
                  </Link>
                </li>
                <li
                  className="breadcrumb-item active text-white"
                  aria-current="page"
                >
                  All Courses
                </li>
                <hr className="section-divider" />
              </ol>
            </div>
          </div>
        </div>
      </section>
      <div className="container py-4">
        {/* Filters */}
        <Row className="g-3">
          <Col md={4}>
            <Form.Label>School</Form.Label>
            <Form.Select
              value={selectedSchool}
              onChange={(e) => {
                setSelectedSchool(e.target.value);
                setSelectedDept("all"); // reset dept when school changes
              }}
            >
              <option value="all">All Schools</option>
              {schools.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.title}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={4}>
            <Form.Label>Department</Form.Label>
            <Form.Select
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
            >
              {departmentOptions.map((v) => (
                <option key={v} value={v}>
                  {v === "all" ? "All Departments" : v}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={4}>
            <Form.Label>Level</Form.Label>
            <Form.Select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
            >
              {levelOptions.map((v) => (
                <option key={v} value={v}>
                  {v === "all" ? "All Levels" : v}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={4}>
            <Form.Label>Mode</Form.Label>
            <Form.Select
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value)}
            >
              {modeOptions.map((v) => (
                <option key={v} value={v}>
                  {v === "all" ? "All Modes" : v}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={4}>
            <Form.Label>Duration</Form.Label>
            <Form.Select
              value={selectedDuration}
              onChange={(e) => setSelectedDuration(e.target.value)}
            >
              {durationBucketsFound.map((v) => (
                <option key={v} value={v}>
                  {v === "all" ? "Any Duration" : v}
                </option>
              ))}
            </Form.Select>
            <div className="form-text">
              Buckets use normalized duration (months) parsed from text.
            </div>
          </Col>

          <Col md={4}>
            <Form.Label>Eligibility (contains)</Form.Label>
            <Form.Control
              type="text"
              placeholder='e.g. "10+2", "Graduation"'
              value={eligibilityText}
              onChange={(e) => setEligibilityText(e.target.value)}
            />
          </Col>

          <Col md={4} className="d-flex align-items-end">
            <Form.Check
              type="checkbox"
              id="raipur-only"
              label="Available at Raipur campus"
              checked={showOnlyRaipur}
              onChange={(e) => setShowOnlyRaipur(e.target.checked)}
            />
          </Col>

          <Col md={4} className="d-flex align-items-end">
            <Form.Check
              type="checkbox"
              id="gariyaband-only"
              label="Available at Gariyaband campus"
              checked={showOnlyGariyaband}
              onChange={(e) => setShowOnlyGariyaband(e.target.checked)}
            />
          </Col>

          <Col md={4} className="d-flex align-items-end">
            <Button
              variant="secondary"
              onClick={() => {
                setSelectedSchool("all");
                setSelectedDept("all");
                setSelectedLevel("all");
                setSelectedMode("all");
                setSelectedDuration("all");
                setEligibilityText("");
                setShowOnlyRaipur(false);
                setShowOnlyGariyaband(false);
              }}
            >
              Reset Filters
            </Button>
          </Col>
        </Row>

        {/* States */}
        <div className="mt-4">
          {loadingSchools && (
            <div className="text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading schools…
            </div>
          )}
          {errorSchools && <div className="text-danger">{errorSchools}</div>}
        </div>

        <div className="mt-2">
          {loadingCourses && (
            <div className="text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading courses…
            </div>
          )}
          {errorCourses && <div className="text-danger">{errorCourses}</div>}
        </div>

        {/* Results */}
        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <strong>{filteredCourses.length}</strong> course
              {filteredCourses.length !== 1 ? "s" : ""} found
            </div>
          </div>

          {filteredCourses.length === 0 && !loadingCourses && (
            <div className="text-muted">No courses match your filters.</div>
          )}

          <Row className="g-3">
            {filteredCourses.map((c) => {
              const months = parseDurationToMonths(c.duration);
              const bucket = durationBucket(months);
              return (
                <Col md={6} lg={4} key={c.id}>
                  <div className="card rounded-3 courseBox p-0 h-100 shadow-lg bg-body border-0">
                    <div className="card-header">
                      <Link
                        to={`/course-details/${encodeURIComponent(c.name)}`}
                        className="text-decoration-none"
                      >
                        <h3 className="card-title font-weight-bold m-0">
                          {c.title}
                        </h3>
                      </Link>
                    </div>
                    <div className="card-body">
                      <div className="col mb-3">
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {c.level && <Badge bg="primary">{c.level}</Badge>}
                          {c.mode && <Badge bg="info">{c.mode}</Badge>}
                          {bucket && <Badge bg="secondary">{bucket}</Badge>}
                          {c.department && (
                            <Badge bg="danger">{c.department}</Badge>
                          )}
                        </div>
                        <Row className="info-line">
                          <i className="ti ti-file-report me-2 align-self-center"></i>
                          <p>
                            Duration: <b>{c.duration}</b>
                          </p>
                        </Row>
                        <Row className="info-line">
                          <i className="ti ti-file-text-shield me-2 align-self-center col-auto"></i>
                          <p className="col-11">
                            Eligibility: <b>{c.eligibility}</b>
                          </p>
                        </Row>
                        <div className="small mt-2">
                          Raipur:{" "}
                          <span
                            className={
                              String(c.raipur_campus || "").includes(
                                "accept.png",
                              )
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            {String(c.raipur_campus || "").includes(
                              "accept.png",
                            )
                              ? "Available"
                              : "Not Available"}
                          </span>{" "}
                          • Gariyaband:{" "}
                          <span
                            className={
                              String(c.gariyaband_campus || "").includes(
                                "accept.png",
                              )
                                ? "text-success"
                                : "text-danger"
                            }
                          >
                            {String(c.gariyaband_campus || "").includes(
                              "accept.png",
                            )
                              ? "Available"
                              : "Not Available"}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex flex-wrap mt-auto">
                        <button
                          type="button"
                          class="btn btn-sm btn-download px-5 me-2"
                          onClick={() => setModalShow2(true)}
                        >
                          Apply Now
                        </button>
                        <Link
                          className="btn btn-sm btn-apply"
                          to={`/course-details/${encodeURIComponent(c.name)}`}
                        >
                          <span className="gdlr-core-content">
                            View Details
                          </span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
            <EnquiryNow show={modalShow2} onHide={() => setModalShow2(false)} />
          </Row>
        </div>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default CourseFinderPage;
